import classNames from 'classnames'
import NativeDateInput from 'components/common/field/NativeDateInput'
import IconRender from 'components/common/IconRender'
import { isMobile } from 'components/utils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'

import QuantityBubble from './QuantityBubble'

interface DateRangeOption {
  label: string
  icon: string
  key: string
}

interface FilterDateRangeProps {
  handleChange: (key: string, value: any) => void
  dateRangeOptions: { [key: string]: DateRangeOption }
  label: string
  icon: string
  filterKey: string
}

const FilterDateRange = ({
  handleChange,
  dateRangeOptions,
  label,
  icon,
  filterKey,
}: FilterDateRangeProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDateRanges, setSelectedDateRanges] = useState<{ [key: string]: { startDate: string, endDate: string } }>({})
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const dropdownRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation();
  const mobile = isMobile();
  const selectedCount = Object.values(selectedDateRanges).filter(range => range.startDate || range.endDate).length

  const handleDateChange = (optionKey: string, type: 'startDate' | 'endDate', value: string) => {
    const newDateRanges = {
      ...selectedDateRanges,
      [optionKey]: {
        ...selectedDateRanges[optionKey],
        [type]: value
      }
    }
    setSelectedDateRanges(newDateRanges)
    handleChange(filterKey, newDateRanges)
  }

  const handleClear = () => {
    const clearedDateRanges: { [key: string]: { startDate: string, endDate: string } } = {}
    Object.keys(dateRangeOptions).forEach(key => {
      clearedDateRanges[key] = { startDate: '', endDate: '' }
    })
    setSelectedDateRanges(clearedDateRanges)
    handleChange(filterKey, clearedDateRanges)
  }

  // const formatDate = (dateString: string) => {
  //   if (!dateString) return ''
  //   const date = new Date(dateString)
  //   return date.toLocaleDateString('en-CA')
  // }

  // const displayText = () => {
  //   if (dateRange.startDate && dateRange.endDate) {
  //     return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
  //   } else if (dateRange.startDate) {
  //     return `From ${formatDate(dateRange.startDate)}`
  //   } else if (dateRange.endDate) {
  //     return `Until ${formatDate(dateRange.endDate)}`
  //   }
  //   return label
  // }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        menuRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      
      // Calculate optimal position to prevent off-screen
      if (dropdownRef.current && !mobile) {
        const buttonRect = dropdownRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const menuMinWidth = 320 // min-w-80 = 320px
        const padding = 20 // margin from screen edge
        
        let left = 0
        let width = buttonRect.width
        
        // Check if menu would go off right edge
        if (buttonRect.left + menuMinWidth > viewportWidth - padding) {
          // Position menu to the left of button
          left = buttonRect.left - menuMinWidth + buttonRect.width
          width = menuMinWidth
          
          // If still off-screen, align to right edge
          if (left < padding) {
            left = viewportWidth - menuMinWidth - padding
            width = menuMinWidth
          }
        }
        
        setMenuStyle({
          top: 50,
          left: left,
          width: width,
          minWidth: menuMinWidth,
          maxWidth: '90vw'
        })
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative text-sm w-full sm:w-fit">
      <button
        ref={dropdownRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={classNames('text-left w-full flex items-center relative pl-10', {
          'h-10 pr-8 rounded-lg justify-end': !mobile,
          'h-12 pr-2 justify-between': mobile,
          'border border-imovPrimary': isOpen && !mobile,
          'border border-gray-300': !isOpen && !mobile,
          'border-b border-gray-300': !isOpen && mobile,
        })}
      >
        <span className="absolute left-3 inset-y-0 flex items-center text-gray-500">
          <IconRender
            icon={icon}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>

        <span className="text-imovPrimary text-md font-semibold whitespace-nowrap px-2">{t(label)}</span>

        <span className={classNames("absolute inset-y-0 flex items-center text-gray-500", {
          "right-2": !mobile,
          "right-10": mobile,
        })}>
           <IconRender
            icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        <QuantityBubble selecteds={selectedCount} />
      </button>

      {(isOpen || mobile) && dropdownRef.current && (
        <div
          ref={menuRef}
          className={classNames(
            "z-50 bg-white border border-gray-200 text-sm p-4 min-w-80 transition-all duration-300 ease-in-out",
            {
              "fixed rounded-md shadow-lg": !mobile,
              "relative": mobile,
              "opacity-0 max-h-0 overflow-hidden": mobile && !isOpen,
              "opacity-100 max-h-[40vh] overflow-y-auto": mobile && isOpen,
            }
          )}
          style={!mobile ? menuStyle : {}}
        >
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">{t(label)}</h3>
            {!mobile && selectedCount > 0 && (
              <div className="flex gap-2 p-2 shadow-sm">
                <button onClick={() => handleClear()} className="flex-1 px-3 py-2 bg-imovPrimary text-white rounded cursor-pointer hover:bg-imovPrimaryLight transition-colors">
                  {t('common.filter')}
                </button>
                <button onClick={() => handleClear()} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded cursor-pointer hover:bg-gray-300 transition-colors">
                  {t('common.clear')}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {Object.keys(dateRangeOptions).map((optionKey: string) => (
              <div key={optionKey} className="border-b border-gray-100 pb-4 last:border-b-0">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <IconRender
                    icon={dateRangeOptions[optionKey].icon}
                    size={IconSizes.SM}
                    color={CustomColorsRef.Primary}
                  />
                  {t(dateRangeOptions[optionKey].label)}
                </h4>

                <div className="flex gap-2 mb-4">
                  <div className="w-1/2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <NativeDateInput
                      value={selectedDateRanges[optionKey]?.startDate || ''}
                      onChange={e => handleDateChange(optionKey, 'startDate', e.target.value)}
                      className="date-input-native w-full cursor-pointer rounded-md border-2 border-imovGray p-2 text-sm focus:border-imovPrimaryLight focus:outline-none"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="w-1/2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <NativeDateInput
                      value={selectedDateRanges[optionKey]?.endDate || ''}
                      onChange={e => handleDateChange(optionKey, 'endDate', e.target.value)}
                      className="date-input-native w-full cursor-pointer rounded-md border-2 border-imovGray p-2 text-sm focus:border-imovPrimaryLight focus:outline-none"
                      min={
                        selectedDateRanges[optionKey]?.startDate || new Date().toISOString().split('T')[0]
                      }
                    />
                  </div>
                </div>

                {/* Quick Selection for this specific option */}
                <div className="border-t pt-3">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">
                    Quick Selection
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const today = new Date()
                        const startDate = new Date(today)
                        const endDate = new Date(
                          today.getFullYear(),
                          today.getMonth() + 1,
                          today.getDate()
                        )
                        const newDateRange = {
                          startDate: startDate.toISOString().split('T')[0],
                          endDate: endDate.toISOString().split('T')[0]
                        }
                        const newDateRanges = {
                          ...selectedDateRanges,
                          [optionKey]: newDateRange
                        }
                        setSelectedDateRanges(newDateRanges)
                        handleChange(filterKey, newDateRanges)
                      }}
                      className="text-xs p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                    >
                      Next Month
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date()
                        const startDate = new Date(today)
                        const endDate = new Date(
                          today.getFullYear(),
                          today.getMonth() + 3,
                          today.getDate()
                        )
                        const newDateRange = {
                          startDate: startDate.toISOString().split('T')[0],
                          endDate: endDate.toISOString().split('T')[0]
                        }
                        const newDateRanges = {
                          ...selectedDateRanges,
                          [optionKey]: newDateRange
                        }
                        setSelectedDateRanges(newDateRanges)
                        handleChange(filterKey, newDateRanges)
                      }}
                      className="text-xs p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                    >
                      Next 3 Months
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date()
                        const startDate = new Date(today)
                        const endDate = new Date(
                          today.getFullYear(),
                          today.getMonth() + 6,
                          today.getDate()
                        )
                        const newDateRange = {
                          startDate: startDate.toISOString().split('T')[0],
                          endDate: endDate.toISOString().split('T')[0]
                        }
                        const newDateRanges = {
                          ...selectedDateRanges,
                          [optionKey]: newDateRange
                        }
                        setSelectedDateRanges(newDateRanges)
                        handleChange(filterKey, newDateRanges)
                      }}
                      className="text-xs p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                    >
                      Next 6 Months
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date()
                        const startDate = new Date(today)
                        const endDate = new Date(
                          today.getFullYear() + 1,
                          today.getMonth(),
                          today.getDate()
                        )
                        const newDateRange = {
                          startDate: startDate.toISOString().split('T')[0],
                          endDate: endDate.toISOString().split('T')[0]
                        }
                        const newDateRanges = {
                          ...selectedDateRanges,
                          [optionKey]: newDateRange
                        }
                        setSelectedDateRanges(newDateRanges)
                        handleChange(filterKey, newDateRanges)
                      }}
                      className="text-xs p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                    >
                      Next Year
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FilterDateRange
